/**
 * AnchorChip, "Remember this spot" affordance on parking QR scans.
 *
 * Quiet, single-chip entry point that upgrades to Saved / Add photo / Add
 * note once tapped. All state lives in localStorage (see @/lib/parking).
 * Never round-trips to the server except optionally for the photo upload.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, CheckCircle2, Loader2, Pencil, X } from 'lucide-react';
import { cn } from '@tendedloop/ui';
import { compressImage, getImagePreviewUrl } from '@/lib/imageCompression';
import { clearAnchor, readAnchor, saveAnchor, updateAnchor, type ParkedSpot } from '@/lib/parking';

interface AnchorChipProps {
  tenantId: string;
  tenantName: string;
  buildingId: string;
  buildingName: string;
  amenityId: string;
  amenityName: string;
  locationName: string;
  level: number | null;
  ttlHours: number;
  className?: string;
}

type Phase = 'idle' | 'saved';

const MAX_NOTE = 100;

export function AnchorChip(props: AnchorChipProps) {
  const { t } = useTranslation('visitor');
  const { tenantId, tenantName, buildingId, buildingName, amenityId, amenityName, locationName, level, ttlHours } =
    props;

  // Initial read, is there already an anchor at this tenant/building for the
  // same amenity? If yes, show saved state; otherwise show idle.
  const [phase, setPhase] = useState<Phase>('idle');
  const [spot, setSpot] = useState<ParkedSpot | null>(null);
  const [noteEditing, setNoteEditing] = useState(false);
  const [draftNote, setDraftNote] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const existing = readAnchor(tenantId, buildingId);
    if (existing && existing.amenityId === amenityId) {
      setPhase('saved');
      setSpot(existing);
      setDraftNote(existing.note || '');
    }
  }, [tenantId, buildingId, amenityId]);

  const handleAnchor = () => {
    const next = saveAnchor({
      tenantId,
      tenantName,
      buildingId,
      buildingName,
      amenityId,
      amenityName,
      locationName,
      level,
      ttlHours,
    });
    setSpot(next);
    setPhase('saved');
    try {
      navigator.vibrate?.([30]);
    } catch {
      /* noop */
    }
  };

  const handleClear = () => {
    clearAnchor(tenantId, buildingId);
    setSpot(null);
    setPhase('idle');
    setDraftNote('');
  };

  const handleNoteBlur = () => {
    setNoteEditing(false);
    const trimmed = draftNote.trim().slice(0, MAX_NOTE);
    if (!spot) return;
    if (trimmed === (spot.note ?? '')) return;
    const next = updateAnchor(tenantId, buildingId, { note: trimmed || undefined });
    if (next) setSpot(next);
  };

  const handlePhoto = async (file: File) => {
    setError(null);
    setPhotoBusy(true);
    try {
      const compressed = await compressImage(file);
      // Photo stays on the device as a compressed dataURL. No network, no
      // identity. Dies when the visitor clears their browser data.
      const dataUrl = await getImagePreviewUrl(compressed);
      const next = updateAnchor(tenantId, buildingId, { photoThumb: dataUrl });
      if (next) setSpot(next);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('v2.anchor.photoError', 'Photo upload failed. Tap add photo to try again.'),
      );
    } finally {
      setPhotoBusy(false);
    }
  };

  if (phase === 'idle') {
    return (
      <button
        type="button"
        onClick={handleAnchor}
        className={cn(
          'mt-4 inline-flex min-h-[48px] items-center gap-2 rounded-full border border-[#E8EAED] bg-white',
          'px-4 py-3 text-[15px] font-medium text-[#0A0E14]',
          'transition-transform active:scale-[0.97]',
          'shadow-[0_4px_12px_-4px_rgba(10,14,20,0.06)]',
          props.className,
        )}
      >
        <span aria-hidden="true">🅿️</span>
        {t('v2.anchor.remember', 'Remember this spot')}
      </button>
    );
  }

  return (
    <div
      className={cn(
        'mt-4 rounded-2xl border border-[#E8EAED] bg-white px-4 py-3',
        'shadow-[0_4px_12px_-4px_rgba(10,14,20,0.06)]',
        'animate-scale-in',
        props.className,
      )}
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-[18px] w-[18px] flex-shrink-0" style={{ color: '#059669' }} aria-hidden="true" />
        <span className="text-[15px] font-semibold text-[#0A0E14]">
          {t('v2.anchor.saved', 'Saved. {{name}}', { name: amenityName })}
        </span>
        <button
          type="button"
          onClick={handleClear}
          aria-label={t('v2.anchor.clear', 'Forget this spot')}
          className="ms-auto flex h-12 w-12 items-center justify-center rounded-full text-[#5A6472] transition-colors hover:bg-[#F3F4F0]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Photo row */}
      {spot?.photoThumb && (
        <div className="mt-3 overflow-hidden rounded-xl border border-[#E8EAED]">
          <img
            src={spot.photoThumb}
            alt={t('v2.anchor.photoAlt', 'Your parking spot')}
            className="h-40 w-full object-cover"
          />
        </div>
      )}

      {/* Actions row */}
      <div className="mt-3 flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handlePhoto(file);
            e.target.value = '';
          }}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={photoBusy}
          className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-[#E8EAED] bg-white px-4 py-3 text-[15px] font-medium text-[#0A0E14] transition-transform active:scale-[0.97] disabled:opacity-50"
        >
          {photoBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Camera className="h-4 w-4" aria-hidden="true" />
          )}
          {spot?.photoThumb ? t('v2.anchor.replacePhoto', 'Replace photo') : t('v2.anchor.addPhoto', 'Add photo')}
        </button>

        {!noteEditing && (
          <button
            type="button"
            onClick={() => setNoteEditing(true)}
            className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-[#E8EAED] bg-white px-4 py-3 text-[15px] font-medium text-[#0A0E14] transition-transform active:scale-[0.97]"
          >
            <Pencil className="h-4 w-4" aria-hidden="true" />
            {spot?.note ? t('v2.anchor.editNote', 'Edit note') : t('v2.anchor.addNote', 'Add note')}
          </button>
        )}
      </div>

      {spot?.note && !noteEditing && <div className="mt-2 text-[15px] font-medium text-[#5A6472]">“{spot.note}”</div>}

      {noteEditing && (
        <textarea
          autoFocus
          dir="auto"
          value={draftNote}
          maxLength={MAX_NOTE}
          onChange={(e) => setDraftNote(e.target.value)}
          onBlur={handleNoteBlur}
          placeholder={t('v2.anchor.notePlaceholder', 'e.g. near orange pillar')}
          className="mt-2 w-full rounded-xl border border-[#E8EAED] bg-white px-3 py-2 text-[15px] text-[#0A0E14] placeholder:text-[#A3ADB8] focus:outline-none focus:ring-2 focus:ring-[#0E7C7B]/30"
          rows={2}
        />
      )}

      {error && (
        <div className="mt-2 text-[15px] text-red-700">
          {t('v2.anchor.photoError', 'Photo upload failed. Tap add photo to try again.')}
        </div>
      )}
    </div>
  );
}
